import { defineStore } from 'pinia'
import { Permission } from '@/types/Permission'
import { MenuType } from '@/enum/global-enums'
import { Menu } from '@/types/Menu'
import { constantRoutes } from '@/routers'
import { Router, RouteRecordRaw } from 'vue-router'
import { Component, toRaw } from 'vue'
import { getModuleViewComponents } from '@/routers/constantRoutes'
import { buildStaticMenuTree } from '@/routers/staticModules'
import { isStaticMode } from '@/routers/mode'
import piniaPersistConfig from '@/config/piniaPersist'

interface Permissions {
  menus: Menu[]
  asyncMenus: Menu[]
  permissions: Permission[]
  menuPathMap: Map<string, string>
  cacheRouter: string[]
  isDynamicRouteLoaded?: boolean
}

const isPagePermission = (permission: Permission) =>
  permission.type === MenuType.PAGE_TYPE || permission.type === MenuType.TOP_TYPE

// 如果你的枚举里暂时没有 BUTTON_TYPE，这里改成 permission.type === 3
const isActionPermission = (permission: Permission) => permission.type === MenuType.BUTTON_TYPE

export const usePermissionsStore = defineStore('PermissionsStore', {
  state: (): Permissions => ({
    menus: [],
    asyncMenus: [],
    permissions: [],
    menuPathMap: new Map(),
    cacheRouter: [],
    isDynamicRouteLoaded: false
  }),

  getters: {
    getMenus: (state) => state.menus,
    getAsyncMenus: (state) => state.asyncMenus,
    getPermissions: (state) => state.permissions,
    getMenuPathMap: (state) => state.menuPathMap,
    getCacheRouter: (state) => state.cacheRouter,
    getDynamicRouteLoaded(): boolean {
      return this.isDynamicRouteLoaded
    },
    /**
     * 静态菜单树：来自子模块 routes.ts + 基座路由（工作台、/bi、菜单同步等）
     * 动态模式下只保留基座部分，子模块菜单由后台权限驱动
     */
    getStaticMenus(): Menu[] {
      return buildStaticMenuTree(isStaticMode())
    }
  },

  actions: {
    /**
     * 根据权限生成 asyncMenus（不持久化）
     * 这里不要再提前把 type=3 过滤掉
     */
    getAsyncMenusFrom(permissions: Permission[], force = false): Menu[] {
      if (!force && this.asyncMenus.length) return this.asyncMenus

      // 工作台已前端写死（constantRoutes），过滤后台返回的同路径菜单，避免重复注册
      const asyncMenus = this.buildMenus(permissions, 0, '').filter(
        (menu) => menu.path.replace(/^\/+/, '') !== 'default'
      )

      this.asyncMenus = asyncMenus
      return this.asyncMenus
    },

    /**
     * 合并 constantRoutes
     */
    getMenusFrom(permissions: Permission[], force = false): Menu[] {
      const asyncMenus = this.getAsyncMenusFrom(permissions, force)
      this.menus = this.transformRoutesToMenus(constantRoutes).concat(toRaw(asyncMenus))
      return this.menus
    },

    /**
     * 获取某个页面下绑定的按钮权限
     * 优先按 parent_id 绑定
     * 若后端出现 parent_id 异常，再用 permission_mark 前缀兜底
     */
    getPageActions(pagePermission: Permission, allPermissions: Permission[]): string[] {
      const directActions = allPermissions
        .filter((item) => {
          return isActionPermission(item) && item.parent_id === pagePermission.id && !!item.permission_mark
        })
        .map((item) => item.permission_mark)
        .filter(Boolean)

      if (directActions.length) {
        return Array.from(new Set(directActions))
      }

      // 兜底：按 permission_mark 前缀匹配
      const pageMark = (pagePermission.permission_mark || '').trim()
      if (!pageMark) return []

      const fallbackActions = allPermissions
        .filter((item) => {
          return (
            isActionPermission(item) &&
            typeof item.permission_mark === 'string' &&
            item.permission_mark.startsWith(`${pageMark}@`)
          )
        })
        .map((item) => item.permission_mark)
        .filter(Boolean)

      return Array.from(new Set(fallbackActions))
    },

    /**
     * 构建菜单
     * 规则：
     * 1. 根级菜单(parent_id === 0)使用 LAYOUT（根级叶子页面带组件时用真实组件）
     * 2. 非根级且有“页面子节点”时使用 PARENT_VIEW
     * 3. 叶子页面使用真实页面组件
     *
     * 注意：
     * hasChildren 只能判断“页面子节点”，不能把按钮子节点算进去，
     * 否则一个仅有按钮权限的页面会被误判成 PARENT_VIEW。
     */
    buildMenus(allPermissions: Permission[], parentId = 0, parentPath = ''): Menu[] {
      const currentLevelPages = allPermissions
        .filter(
          (item) => isPagePermission(item) && item.parent_id === parentId && item.module !== 'common'
        )
        .sort((a, b) => Number(b.sort || 0) - Number(a.sort || 0))

      const menus: Menu[] = []

      currentLevelPages.forEach((permission) => {
        const routePart = (permission.route || '').replace(/\//g, '_')
        const markPart = permission.permission_mark || `permission_${permission.id}`
        const name = `${permission.module === 'default' ? 'default_' : ''}${markPart}${routePart}`

        const currentPath = this.resolveRoutePath(permission.route, parentPath)

        // 这里只判断页面子级，不能把按钮算进去
        const childPages = allPermissions.filter((item) => {
          return isPagePermission(item) && item.parent_id === permission.id
        })
        const hasChildren = childPages.length > 0

        const actions = this.getPageActions(permission, allPermissions)

        const rawComp = permission.component || ''
        let compKey = rawComp

        // 动态菜单整体作为 'Layout' 路由的子路由渲染：
        // - 任意父级（含根级父级）仅作为 <router-view> 容器，使用 PARENT_VIEW
        // - 根级叶子页面（如工作台）带有真实组件时直接使用真实组件
        // 最外层框架（侧边栏/顶栏）由 'Layout' 路由统一提供，避免布局嵌套
        if (hasChildren || !rawComp) {
          compKey = 'PARENT_VIEW'
        }

        const menu: Menu = {
          path: currentPath,
          name,
          componentKey: compKey,
          redirect: permission.redirect || undefined,
          meta: {
            title: permission.permission_name,
            icon: permission.icon,
            keepalive: permission.keepalive,
            hidden: permission.hidden,
            active_menu: permission.active_menu,
            is_blank: permission.is_blank,
            permission_mark: permission.permission_mark,
            actions
          }
        }

        const children = this.buildMenus(allPermissions, permission.id, currentPath)
        if (children.length) {
          menu.children = children
        }

        menus.push(menu)
      })

      return menus
    },

    /**
     * 刷新页面时初始化动态路由
     */
    initRoutesOnRefresh(router: Router) {
      // 静态模式：路由已通过 routes.ts 全量注册，不需要根据权限注入
      if (isStaticMode()) return
      if (!this.permissions.length) return
      if (this.isDynamicRouteLoaded) return

      const asyncMenus = this.getAsyncMenusFrom(this.permissions, true)

      this.restoreComponents(asyncMenus)

      asyncMenus.forEach((menu: Menu) => {
        router.addRoute('Layout', menu as RouteRecordRaw)
      })

      if (!router.hasRoute('not-found')) {
        router.addRoute({
          path: '/:pathMatch(.*)*',
          name: 'not-found',
          meta: { title: '404' },
          component: () => import('@/components/404/index.vue')
        })
      }

      this.setDynamicRouteLoaded(true)
    },

    /**
     * 运行时恢复 component
     */
    restoreComponents(menus: Menu[]) {
      const viewComponents = getModuleViewComponents()

      menus.forEach((menu) => {
        if (menu.componentKey) {
          if (menu.componentKey === 'LAYOUT') {
            menu.component = () => import('@/layout/index.vue')
          } else if (menu.componentKey === 'PARENT_VIEW') {
            menu.component = () => import('@/layout/routerView.vue')
          } else {
            const comp = viewComponents.get(menu.componentKey)
            if (!comp) {
              console.error('组件未找到:', menu.componentKey, menu.path)
            }
            menu.component = comp
          }
        }

        if (menu.children?.length) {
          this.restoreComponents(menu.children)
        }
      })
    },

    /**
     * constantRoutes 转 menu
     */
    transformRoutesToMenus(routes: Menu[] | RouteRecordRaw[], parentPath = ''): Menu[] {
      const menus: Menu[] = []

      routes.forEach((route) => {
        const menu: Menu = {
          path: this.resolveRoutePath(route.path as string, parentPath),
          name: route.name as string,
          component: route.component as Component | undefined,
          meta: route.meta
        }

        if (route.children?.length) {
          menu.children = this.transformRoutesToMenus(route.children, menu.path)
        }

        menus.push(menu)
      })

      return menus
    },

    /**
     * 当前页面是否拥有某个按钮权限
     */
    hasAction(actionMark: string, actions: string[] = []) {
      return actions.includes(actionMark)
    },

    setCacheRouter(routes: string[]) {
      this.cacheRouter = routes
    },

    setDynamicRouteLoaded(status: boolean) {
      this.isDynamicRouteLoaded = status
    },

    resolveRoutePath(route: string, parentPath: string): string {
      if (!parentPath) return route.replace(/\/$/, '')
      return (parentPath + '/' + route).replace(/\/+/g, '/').replace(/\/$/, '')
    }
  },

  persist: piniaPersistConfig(import.meta.env.VITE_META_CODE + 'PermissionsStore', [
    'permissions',
    'cacheRouter',
    'menuPathMap'
  ])
})
